<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import { onMount } from "svelte";
	import type { HTMLDialogAttributes } from "svelte/elements";
	import cross from "@core/assets/icons/cross.svg?raw";
	import { Locale } from "@core/schema/Locale.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";

	let {
		/* eslint-disable prefer-const */
		show = $bindable(false),
		maxWidth = "max-w-lg",
		...rest
		/* eslint-enable prefer-const */
	}: HTMLDialogAttributes & { show: boolean; maxWidth?: string } = $props();

	const { tenant } = getGlobalTemplateProps();
	const closeLabel = tenant.locale === Locale.cs ? "Zavřít" : "Close";

	let dialog = $state<HTMLDialogElement | null>(null);

	$effect(() => {
		if (!dialog) {
			return;
		}

		if (show && !dialog.open) {
			dialog.showModal();
		}
		if (!show && dialog.open) {
			dialog.close();
		}
	});

	$effect(() => {
		if (show && !lockScroll.lock) {
			lockScroll.lock = true;
		}
	});
	$effect(() => {
		if (!show) {
			lockScroll.lock = false;
		}
	});

	function closeOnBackdrop(event: MouseEvent): void {
		if (!event.target) {
			return;
		}

		if (!(event.target instanceof Node)) {
			return;
		}

		if (event.target.nodeName === "DIALOG") {
			show = false;
		}
	}

	// Not using on:click because svelte complains about accessibility (non-interactable elements with a mouse/keyboard event listener).
	onMount(() => {
		dialog?.addEventListener("click", closeOnBackdrop);
		return (): void => {
			dialog?.removeEventListener("click", closeOnBackdrop);
		};
	});
</script>

<dialog
	bind:this={dialog}
	{...rest}
	class={["mx-auto w-full", maxWidth, "bg-transparent p-4", rest.class]}
	onclose={(event: Event & { currentTarget: EventTarget & HTMLDialogElement }): void => {
		show = false;
		rest.onclose?.(event);
	}}
>
	<!-- This div element makes closing on backdrop work correctly -->
	<div class="modal-panel shadow-elevated relative max-h-[85vh] overflow-y-auto rounded-3xl bg-white p-6 pt-12 sm:p-8">
		{@render rest.children?.()}

		<button
			type="button"
			aria-label={closeLabel}
			class="text-ink-600 hover:text-ink-900 bg-ink-100 hover:bg-ink-200 focus-visible:ring-secondary/40 absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-4"
			onclick={(): void => {
				show = false;
			}}
		>
			<Icon icon={cross} class="w-3.5" square />
		</button>
	</div>
</dialog>

<style>
	/* Dim + blur the page behind the modal so it reads as a focused, trustworthy overlay. */
	dialog::backdrop {
		background: rgba(24, 23, 23, 0.5);
		-webkit-backdrop-filter: blur(5px);
		backdrop-filter: blur(5px);
	}

	dialog[open]::backdrop {
		animation: modal-backdrop-in 220ms ease-out;
	}

	dialog[open] .modal-panel {
		animation: modal-panel-in 260ms cubic-bezier(0.16, 1, 0.3, 1);
	}

	@keyframes modal-backdrop-in {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes modal-panel-in {
		from {
			opacity: 0;
			transform: translateY(14px) scale(0.96);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		dialog[open]::backdrop,
		dialog[open] .modal-panel {
			animation: none;
		}
	}
</style>
